import { useLingui } from '@lingui/react/macro'
import {
  executeDocumentPrint,
  isSafariBrowser,
  type PrintPageRange,
} from 'features/knowledgeDocumentViewer/utils/printDocument'
import { useRef, useState } from 'react'

const PRINT_PLACEHOLDER_HTML_OPEN =
  '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui,sans-serif;color:#666;">'
const PRINT_PLACEHOLDER_HTML_CLOSE = '</body></html>'

type UseDocumentPrintActionsArgs = {
  pdfSignedUrl: string | undefined
  documentType: string | undefined
  currentPageNumber: number
  currentPageImageUrl: string | undefined
}

export function useDocumentPrintActions({
  pdfSignedUrl,
  documentType,
  currentPageNumber,
  currentPageImageUrl,
}: UseDocumentPrintActionsArgs) {
  const { t } = useLingui()
  const isPrintingRef = useRef(false)
  const [isPreparing, setIsPreparing] = useState(false)

  const runPrint = async (options?: {
    pageRange?: PrintPageRange
    preferCurrentPageImage?: boolean
  }) => {
    if (!pdfSignedUrl || isPrintingRef.current) return

    isPrintingRef.current = true
    setIsPreparing(true)

    const preOpenedWindow = isSafariBrowser()
      ? window.open('about:blank', '_blank')
      : null

    if (preOpenedWindow) {
      const preparingMessage = t`Preparing document for printing…`
      preOpenedWindow.document.write(
        PRINT_PLACEHOLDER_HTML_OPEN +
          preparingMessage +
          PRINT_PLACEHOLDER_HTML_CLOSE
      )
      preOpenedWindow.document.close()
    }

    try {
      await executeDocumentPrint({
        signedUrl: pdfSignedUrl,
        documentType,
        pageRange: options?.pageRange,
        currentPageImageUrl: options?.preferCurrentPageImage
          ? currentPageImageUrl
          : undefined,
        preOpenedWindow,
      })
    } catch (error) {
      if (preOpenedWindow && !preOpenedWindow.closed) {
        preOpenedWindow.close()
      }
      console.error('Failed to print document:', error)
    } finally {
      isPrintingRef.current = false
      setIsPreparing(false)
    }
  }

  const handleCurrentPagePrint = () => {
    void runPrint({
      pageRange: {
        startPage: currentPageNumber,
        endPage: currentPageNumber,
      },
      preferCurrentPageImage: true,
    })
  }

  const handleRangePrint = (startPage: number, endPage: number) => {
    void runPrint({
      pageRange: { startPage, endPage },
      preferCurrentPageImage:
        startPage === currentPageNumber && endPage === currentPageNumber,
    })
  }

  const handleFullDocumentPrint = () => {
    void runPrint()
  }

  return {
    isPreparing,
    handleCurrentPagePrint,
    handleRangePrint,
    handleFullDocumentPrint,
  }
}
